import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Sparkline, Tooltip } from "@lopie/kernel-ui";

/**
 * What an agent has spent, against what it is allowed to.
 *
 * ## Two pictures, because there are two questions
 *
 * The first version of this was going to be one chart, and it would have been
 * wrong on a scale problem: a limit of $25 over thirty days and a run that
 * cost $0.87 are not on the same axis, so a threshold line drawn across a
 * per-run bar chart sits off the top of it and means nothing. They are two
 * questions and they get two shapes.
 *
 * **Am I within budget** is cumulative — one bar, the window's total against
 * the limit, with the warning marked on the track. **Is this run unusual** is
 * a sequence — one bar per run, which is the thing `#SPEND` could never
 * answer because it accumulates.
 *
 * ## Colour the frame, never the data — with one deliberate exception
 *
 * The track is the budget and it carries the thresholds; the bars in the
 * series are neutral, because a run is not good or bad on its own. The
 * *fill* of the budget bar does take the semantic colour, and that is not a
 * slip: ok, warning and over are governance states in the same family as
 * permitted and refused, and this product colours those everywhere. The word
 * sits beside it in every case, so the colour is never the only signal.
 *
 * ## Nothing is drawn that is not a sequence
 *
 * Under two runs there is no shape, and the component says so in words rather
 * than drawing a single bar that reads as a trend. Same rule the Activity
 * screen states: one write, or writes all inside one bucket, is a number.
 */

const PERIODS = [
  ["day", "a day"],
  ["week", "a week"],
  ["month", "30 days"],
  ["total", "all time"],
];

const TONE = {
  ok: "kernel.permitted",
  warning: "kernel.attention",
  over: "kernel.refused",
};

function dollars(usd, places = 2) {
  const n = Number(usd) || 0;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  })}`;
}

function shortDate(iso) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso || "";
  return at.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/**
 * The cumulative bar: the window's spend against the limit.
 *
 * The track is the whole limit and the tick is the warning, so the distance
 * left to run is a thing you see rather than a subtraction you do.
 */
function BudgetBar({ budget, standing }) {
  const limit = budget?.limit_usd || 0;
  const warn = budget?.warn_usd || 0;
  const tone = TONE[standing.state] || TONE.ok;
  const share = Math.min(standing.share || 0, 1) * 100;

  if (!limit) return null;

  return (
    <Box>
      <Box
        role="img"
        aria-label={
          `${dollars(standing.spent)} of ${dollars(limit)} spent `
          + `${budget.period_label}.`
        }
        sx={{
          position: "relative",
          height: 12,
          borderRadius: 999,
          backgroundColor: "action.hover",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${share}%`,
            height: "100%",
            backgroundColor: tone,
            transition: "width 220ms ease-out",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
        {warn && warn < limit ? (
          // The warning lives on the track, not on the fill. It is a property
          // of the budget and it stays in the same place as the spend moves.
          <Tooltip title={`Warns at ${dollars(warn)}`}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${(warn / limit) * 100}%`,
                width: 2,
                backgroundColor: "background.paper",
                opacity: 0.9,
              }}
            />
          </Tooltip>
        ) : null}
      </Box>

      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", mt: 0.75, flexWrap: "wrap" }}
      >
        <Typography variant="body2" sx={{ color: tone, fontWeight: 600 }}>
          {dollars(standing.spent)} spent {budget.period_label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {standing.remaining !== null && standing.remaining !== undefined
            ? `${dollars(standing.remaining)} left of ${dollars(limit)}`
            : `past ${dollars(limit)}`}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * The series: one bar per run, oldest on the left.
 *
 * Refuses to draw under two runs. A single bar is a number wearing a chart's
 * clothes, and the thing somebody reads off a shape — *this one was unlike
 * the others* — needs others to be unlike.
 */
function RunStrip({ series }) {
  if (!series?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No run has been recorded in this window, so there is nothing to price
        and nothing to draw.
      </Typography>
    );
  }
  if (series.length < 2) {
    return (
      <Typography variant="body2" color="text.secondary">
        One run in this window, at {dollars(series[0].usd, 4)}. Not enough to
        draw a shape — a single bar reads as a trend and is a number.
      </Typography>
    );
  }

  const values = series.map((r) => r.usd);
  const most = Math.max(...values);
  const dearest = series[values.indexOf(most)];

  return (
    <Box>
      <Sparkline
        values={values}
        labels={series.map(
          (r) => `${shortDate(r.at)} — ${dollars(r.usd, 4)}`
            + (r.priced ? "" : " (no price for that model)"),
        )}
        tone="text.secondary"
        height={44}
        label={
          `One bar per run, ${series.length} of them, oldest first. `
          + `The dearest cost ${dollars(most, 4)}.`
        }
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.75 }}
      >
        {series.length} runs, oldest first. Bars are relative to the dearest —{" "}
        {dollars(most, 4)} on {shortDate(dearest.at)} — because there is no
        axis here that was measured against anything.
      </Typography>
    </Box>
  );
}

export default function Spending({ agent, onBudget, busy }) {
  const budget = agent?.budget || null;
  const standing = agent?.standing || { state: "ok", spent: 0, share: 0 };
  const series = agent?.runs_series || [];

  const [warn, setWarn] = useState("");
  const [limit, setLimit] = useState("");
  const [period, setPeriod] = useState("month");

  // Re-seeded on the agent's name, not its object identity — the screen
  // behind this polls, and a draft wiped mid-edit by a refresh is the bug
  // this pattern already cost once.
  useEffect(() => {
    setWarn(budget?.warn_usd ? String(budget.warn_usd) : "");
    setLimit(budget?.limit_usd ? String(budget.limit_usd) : "");
    setPeriod(budget?.period || "month");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.name]);

  const changed =
    warn !== (budget?.warn_usd ? String(budget.warn_usd) : "")
    || limit !== (budget?.limit_usd ? String(budget.limit_usd) : "")
    || period !== (budget?.period || "month");

  const save = () =>
    onBudget?.({
      warn_usd: warn === "" ? 0 : Number(warn),
      limit_usd: limit === "" ? 0 : Number(limit),
      period,
    });

  return (
    <Stack spacing={2.5}>
      {budget ? (
        <BudgetBar budget={budget} standing={standing} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No limit set. It can spend whatever it spends, and the only thing
          that stops it is somebody noticing.
        </Typography>
      )}

      {standing.state === "over" ? (
        <Alert severity="error" variant="outlined">
          <AlertTitle>Stopped — it is past its limit</AlertTitle>
          Nothing this kernel starts will start it: not the schedule, not the
          Run button. Raise the limit or wait for the window to roll forward.
        </Alert>
      ) : standing.state === "warning" ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Past the warning, still running</AlertTitle>
          This is the notice, not the stop. It keeps working until{" "}
          {dollars(budget.limit_usd)}, which is the point of having two
          numbers rather than one.
        </Alert>
      ) : null}

      {/* Lifetime spend with nothing in the window is a window that is
          blind rather than empty. Per-run rows started when this feature
          landed; every run before then is in the accumulated total and
          nowhere else, and a small number that is really an absence is the
          one a budget must not be quietly enforced against. */}
      {standing.whole === false ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>This window cannot see all of itself</AlertTitle>
          This agent has spent {dollars(agent?.spend?.usd || 0)} in total, and
          none of it is in the run-by-run records — those began when limits
          did, so everything before that is in the lifetime figure and nowhere
          else. A windowed limit starts counting from the next run. Measure
          over <strong>all time</strong> to include what came before.
        </Alert>
      ) : null}

      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          What it has cost, run by run
        </Typography>
        <RunStrip series={series} />
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Set a limit
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 2 }}>
          <TextField
            size="small"
            label="Warn at"
            value={warn}
            disabled={busy || !onBudget}
            onChange={(e) => setWarn(e.target.value.replace(/[^\d.]/g, ""))}
            sx={{ width: 150 }}
            slotProps={{ input: { startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> } }}
            helperText="Flags it. Keeps running."
          />
          <TextField
            size="small"
            label="Stop at"
            value={limit}
            disabled={busy || !onBudget}
            onChange={(e) => setLimit(e.target.value.replace(/[^\d.]/g, ""))}
            sx={{ width: 150 }}
            slotProps={{ input: { startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> } }}
            helperText="Refuses every run."
          />
          <TextField
            select
            size="small"
            label="Measured over"
            value={period}
            disabled={busy || !onBudget}
            onChange={(e) => setPeriod(e.target.value)}
            sx={{ width: 170 }}
            helperText="Rolling, not calendar."
          >
            {PERIODS.map(([id, label]) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
          <Button
            variant="contained"
            size="small"
            disabled={busy || !onBudget || !changed}
            onClick={save}
          >
            Save limit
          </Button>
          {budget ? (
            <Button
              size="small"
              disabled={busy || !onBudget}
              onClick={() => {
                setWarn("");
                setLimit("");
                onBudget?.({ warn_usd: 0, limit_usd: 0, period });
              }}
            >
              Remove limit
            </Button>
          ) : null}
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5, maxWidth: "62ch" }}
        >
          The window rolls rather than resetting on a date — a calendar month
          would clear the budget at midnight on the first, which is exactly
          when a limit is doing the most work. Every figure is worked out from
          what each run reported at Anthropic&apos;s published prices, so this
          controls what this kernel does rather than what a bill says.
        </Typography>
      </Box>

      {/* The boundary, said here because a limit reads as a guarantee
          otherwise. Same sentence the off switch carries, and it is worth
          saying twice rather than letting somebody assume it. */}
      <Alert severity="info" variant="outlined">
        <AlertTitle>What a limit does not do</AlertTitle>
        It stops runs <strong>this kernel starts</strong>. Anything holding the
        app&apos;s own credentials and calling Anthropic directly is not
        stopped by it, and the first this kernel would know is the cost filed
        afterwards.
      </Alert>
    </Stack>
  );
}
